import React, { useState } from 'react'
import { 
  Plus, 
  Trash2, 
  Calendar,
  Percent,
  DollarSign,
  AlertCircle,
  Info
} from 'lucide-react'

interface CondicaoEspecial {
  mes_inicio: number
  mes_fim: number
  tipo_condicao: 'desconto_percentual' | 'desconto_fixo' | 'valor_fixo'
  valor: number
  descricao?: string
}

interface CondicoesEspeciaisProps {
  condicoes: CondicaoEspecial[]
  onChange: (condicoes: CondicaoEspecial[]) => void
  valorBase?: number
}

const CondicoesEspeciais: React.FC<CondicoesEspeciaisProps> = ({ 
  condicoes, 
  onChange,
  valorBase = 0
}) => {
  const [novaCondicao, setNovaCondicao] = useState<CondicaoEspecial>({
    mes_inicio: 1,
    mes_fim: 1,
    tipo_condicao: 'desconto_percentual',
    valor: 0,
    descricao: ''
  })
  const [valorDisplay, setValorDisplay] = useState('')
  const [isEditingValor, setIsEditingValor] = useState(false)

  const adicionarCondicao = () => {
    // Validações
    if (novaCondicao.mes_inicio < 1) {
      alert('Mês de início deve ser maior que 0')
      return
    }

    if (novaCondicao.mes_fim < novaCondicao.mes_inicio) {
      alert('Mês de fim deve ser maior ou igual ao mês de início')
      return
    }

    if (novaCondicao.valor <= 0) {
      alert('Valor deve ser maior que zero')
      return
    }

    // Verificar sobreposição de períodos
    const temSobreposicao = condicoes.some(condicao => {
      return !(novaCondicao.mes_fim < condicao.mes_inicio || novaCondicao.mes_inicio > condicao.mes_fim)
    })

    if (temSobreposicao) {
      alert('Este período se sobrepõe com uma condição existente')
      return
    }

    const novasCondicoes = [...condicoes, { ...novaCondicao }]
    onChange(novasCondicoes)

    // Reset do formulário
    setNovaCondicao({
      mes_inicio: Math.max(...novasCondicoes.map(c => c.mes_fim)) + 1,
      mes_fim: Math.max(...novasCondicoes.map(c => c.mes_fim)) + 3,
      tipo_condicao: 'desconto_percentual',
      valor: 50,
      descricao: ''
    })
  }

  const removerCondicao = (index: number) => {
    const novasCondicoes = condicoes.filter((_, i) => i !== index)
    onChange(novasCondicoes)
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  const calcularValorFinal = (condicao: CondicaoEspecial) => {
    if (!valorBase) return 0

    switch (condicao.tipo_condicao) {
      case 'desconto_percentual':
        return valorBase * (1 - condicao.valor / 100)
      case 'desconto_fixo':
        return Math.max(0, valorBase - condicao.valor)
      case 'valor_fixo':
        return condicao.valor
      default:
        return valorBase
    }
  }

  const getTipoLabel = (tipo: string) => {
    switch (tipo) {
      case 'desconto_percentual':
        return 'Desconto %'
      case 'desconto_fixo':
        return 'Desconto R$'
      case 'valor_fixo':
        return 'Valor Fixo'
      default:
        return tipo
    }
  }

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'desconto_percentual':
        return <Percent className="w-4 h-4" />
      case 'desconto_fixo':
      case 'valor_fixo':
        return <DollarSign className="w-4 h-4" />
      default:
        return <AlertCircle className="w-4 h-4" />
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Condições Especiais</h3>
        <div className="flex items-center text-sm text-gray-500">
          <Info className="w-4 h-4 mr-1" />
          Descontos por período específico
        </div>
      </div>

      {/* Lista de condições existentes */}
      {condicoes.length > 0 && (
        <div className="space-y-3">
          {condicoes.map((condicao, index) => (
            <div key={index} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <Calendar className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-gray-900">
                      Mês {condicao.mes_inicio} ao {condicao.mes_fim}
                    </span>
                    <div className="flex items-center text-blue-600">
                      {getTipoIcon(condicao.tipo_condicao)}
                      <span className="ml-1 text-sm font-medium">
                        {getTipoLabel(condicao.tipo_condicao)}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Condição:</span>
                      <div className="font-medium">
                        {condicao.tipo_condicao === 'desconto_percentual' && `${condicao.valor}% de desconto`}
                        {condicao.tipo_condicao === 'desconto_fixo' && `${formatCurrency(condicao.valor)} de desconto`}
                        {condicao.tipo_condicao === 'valor_fixo' && `Valor fixo de ${formatCurrency(condicao.valor)}`}
                      </div>
                    </div>

                    {valorBase > 0 && (
                      <div>
                        <span className="text-gray-600">Valor final:</span>
                        <div className="font-medium text-green-600">
                          {formatCurrency(calcularValorFinal(condicao))}
                        </div>
                      </div>
                    )}

                    {condicao.descricao && (
                      <div>
                        <span className="text-gray-600">Descrição:</span>
                        <div className="font-medium">{condicao.descricao}</div>
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => removerCondicao(index)}
                  className="ml-4 text-red-600 hover:text-red-800 transition-colors"
                  title="Remover condição"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Formulário para nova condição */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-md font-medium text-gray-900 mb-4">Adicionar Nova Condição</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Período */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mês Início
            </label>
            <input
              type="number"
              min="1"
              value={novaCondicao.mes_inicio}
              onChange={(e) => setNovaCondicao({
                ...novaCondicao,
                mes_inicio: parseInt(e.target.value) || 1
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mês Fim
            </label>
            <input
              type="number"
              min={novaCondicao.mes_inicio}
              value={novaCondicao.mes_fim}
              onChange={(e) => setNovaCondicao({
                ...novaCondicao,
                mes_fim: parseInt(e.target.value) || novaCondicao.mes_inicio
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>

          {/* Tipo de condição */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo
            </label>
            <select
              value={novaCondicao.tipo_condicao}
              onChange={(e) => setNovaCondicao({
                ...novaCondicao,
                tipo_condicao: e.target.value as CondicaoEspecial['tipo_condicao']
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="desconto_percentual">Desconto %</option>
              <option value="desconto_fixo">Desconto R$</option>
              <option value="valor_fixo">Valor Fixo</option>
            </select>
          </div>

          {/* Valor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {novaCondicao.tipo_condicao === 'desconto_percentual' ? 'Percentual (%)' : 'Valor (R$)'}
            </label>
            <div className="relative">
              {novaCondicao.tipo_condicao === 'desconto_percentual' ? (
                <Percent className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              ) : (
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">R$</span>
              )}
              <input
                type="text"
                value={valorDisplay}
                onChange={(e) => {
                  if (novaCondicao.tipo_condicao === 'desconto_percentual') {
                    // Para percentual, só permite números e limita a 100
                    const numbersOnly = e.target.value.replace(/\D/g, '');
                    const numericValue = Math.min(parseInt(numbersOnly) || 0, 100);
                    
                    setValorDisplay(numericValue.toString());
                    setNovaCondicao({
                      ...novaCondicao,
                      valor: numericValue
                    });
                  } else {
                    // Para valor fixo, aplica formatação de moeda
                    const numbersOnly = e.target.value.replace(/\D/g, '');
                    
                    if (numbersOnly === '') {
                      setValorDisplay('');
                      setNovaCondicao({
                        ...novaCondicao,
                        valor: 0
                      });
                      return;
                    }
                    
                    // Converte para número (centavos)
                    const numericValue = parseInt(numbersOnly) / 100;
                    
                    // Formata em tempo real como moeda brasileira
                    const formatted = numericValue.toLocaleString('pt-BR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    });
                    
                    setValorDisplay(formatted);
                    setNovaCondicao({
                      ...novaCondicao,
                      valor: numericValue
                    });
                  }
                }}
                onFocus={() => {
                  setIsEditingValor(true);
                  if (novaCondicao.tipo_condicao === 'desconto_percentual') {
                    setValorDisplay(novaCondicao.valor > 0 ? novaCondicao.valor.toString() : '');
                  } else {
                    if (novaCondicao.valor === 0) {
                      setValorDisplay('');
                    } else {
                      setValorDisplay(novaCondicao.valor.toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      }));
                    }
                  }
                }}
                onBlur={() => {
                  setIsEditingValor(false);
                  // Garante formatação final
                  if (novaCondicao.tipo_condicao === 'desconto_percentual') {
                    setValorDisplay(novaCondicao.valor > 0 ? novaCondicao.valor.toString() : '');
                  } else {
                    if (novaCondicao.valor > 0) {
                      setValorDisplay(novaCondicao.valor.toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      }));
                    }
                  }
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder={novaCondicao.tipo_condicao === 'desconto_percentual' ? '0' : '0,00'}
              />
            </div>
          </div>
        </div>

        {/* Descrição */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descrição (opcional)
          </label>
          <input
            type="text"
            value={novaCondicao.descricao}
            onChange={(e) => setNovaCondicao({
              ...novaCondicao,
              descricao: e.target.value
            })}
            placeholder="Ex: Desconto de lançamento, Promoção primeiros meses..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
        </div>

        {/* Preview do valor final - só mostra se usuário configurou desconto */}
        {valorBase > 0 && novaCondicao.valor > 0 && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between text-sm">
              <span className="text-green-700">Preview - Valor com esta condição:</span>
              <span className="font-semibold text-green-800">
                {formatCurrency(calcularValorFinal(novaCondicao))}
              </span>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={adicionarCondicao}
          className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Adicionar Condição</span>
        </button>
      </div>

      {condicoes.length === 0 && (
        <div className="text-center py-6 text-gray-500">
          <Calendar className="mx-auto h-8 w-8 text-gray-400 mb-2" />
          <p className="text-sm">Nenhuma condição especial configurada</p>
          <p className="text-xs text-gray-400">
            Adicione condições para aplicar descontos em períodos específicos
          </p>
        </div>
      )}
    </div>
  )
}

export default CondicoesEspeciais
